import { Lock } from "lucide-react";

/**
 * Banner shown in `tournament` / `wager` modes when the pilot has at
 * least one artifact equipped, so they understand their powers are
 * intentionally off for fair PvP. Hidden when no artifact is equipped.
 *
 * Extracted from `GameCanvas.jsx` (2026-05-13) — pure UI, no game-loop
 * dependencies. Renders nothing when `competitive` is false or both
 * slots are empty.
 */
export function ArtifactsDisabledBanner({ competitive, paddle, ball, mode }) {
  if (!competitive || (!paddle && !ball)) return null;
  return (
    <div
      className="w-full max-w-[800px] -mt-2 px-3 py-2 border border-[#ffae00]/40 bg-[#ffae00]/8 flex items-center gap-2"
      data-testid="artifacts-disabled-banner"
    >
      <Lock className="w-3.5 h-3.5 text-[#ffae00] shrink-0" />
      <div className="font-mono-cosmic text-[10px] tracking-[0.15em] text-[#ffae00]/90 leading-snug">
        ARTIFACTS DISABLED ·
        <span className="text-white/65 ml-1 normal-case tracking-normal">
          {mode === "tournament"
            ? "Bracket matches are pure skill — your equipped paddle + ball powers won't fire in this run."
            : "Wager runs are head-to-head only — your equipped paddle + ball powers won't fire in this run."}
        </span>
      </div>
    </div>
  );
}

/**
 * Live opponent score pill shown above the canvas during tournament
 * matches. Polled every 3s by Play.jsx and passed in via `opponentLive`.
 *
 * `opponentLive` shape: { name, is_bot, score, waiting }
 *  - `waiting=true` → opponent is still playing (renders "Playing…")
 *  - `waiting=false` → renders their submitted score
 *
 * Extracted from `GameCanvas.jsx` (2026-05-13). Renders nothing when
 * `mode !== "tournament"` or `opponentLive` is null/undefined.
 */
export function TournamentOpponentHud({ mode, opponentLive }) {
  if (mode !== "tournament" || !opponentLive) return null;
  return (
    <div
      className="w-full max-w-[800px] -mt-2 mb-2 px-3 py-1.5 border border-[#3aa8ff]/45 bg-[#3aa8ff]/8 flex items-center justify-between gap-2"
      data-testid="tournament-opponent-hud"
    >
      <div className="font-mono-cosmic text-[9px] tracking-[0.25em] text-[#3aa8ff]/85 uppercase">
        VS {opponentLive.name}
        {opponentLive.is_bot && <span className="ml-1 text-white/40">[BOT]</span>}
      </div>
      <div className="font-mono-cosmic text-xs tabular-nums text-white">
        {opponentLive.waiting ? (
          <span className="text-white/55">Playing…</span>
        ) : (
          <>
            <span className="text-white/55">Score:</span>{" "}
            <span className="text-[#3aa8ff]">
              {Number(opponentLive.score || 0).toLocaleString()}
            </span>
          </>
        )}
      </div>
    </div>
  );
}
